import React, { useEffect, useState } from 'react'
import { ViewMode, ThemeMode } from '../../types'

interface BottomNavigationProps {
  activeTab: 'research' | 'explore' | 'saved' | 'profile'
  onTabChange: (tab: 'research' | 'explore' | 'saved' | 'profile') => void
  viewMode: ViewMode
  themeMode: ThemeMode
  onThemeChange: (theme: ThemeMode) => void
  isVisible?: boolean
  hasBillingIssue?: boolean
}

export default function BottomNavigation({ 
  activeTab, 
  onTabChange, 
  viewMode, 
  themeMode, 
  onThemeChange,
  isVisible = true,
  hasBillingIssue = false
}: BottomNavigationProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [modalOpen, setModalOpen] = useState<boolean>(typeof document !== 'undefined' && document.body.getAttribute('data-modal-open') === 'true')

  useEffect(() => {
    const handler = () => {
      const open = document.body.getAttribute('data-modal-open') === 'true'
      setModalOpen(open)
    }
    window.addEventListener('modal-open-changed', handler as any)
    return () => window.removeEventListener('modal-open-changed', handler as any)
  }, [])

  const navItems = [
    {
      id: 'research' as const,
      label: 'Research',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      )
    },
    {
      id: 'explore' as const,
      label: 'Explore',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'saved' as const,
      label: 'Saved',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      )
    },
    {
      id: 'profile' as const,
      label: 'Profile',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    }
  ]

  const handleThemeToggle = () => {
    onThemeChange(themeMode === 'dark' ? 'light' : 'dark')
  }

  // Mobile bottom navigation
  if (viewMode === 'mobile') {
    return (
      <div className={`fixed bottom-6 left-4 right-4 z-[100] transition-transform duration-300 bottom-nav-mobile ${
        (isVisible && !modalOpen) ? 'translate-y-0' : 'translate-y-[calc(100%+24px)] pointer-events-none'
      }`}>
        <div className="bg-white/50 dark:bg-gray-900/50 backdrop-blur-2xl backdrop-saturate-150 border border-gray-200/30 dark:border-gray-700/30 rounded-3xl shadow-2xl shadow-black/10 dark:shadow-black/20 px-3 py-3">
          <div className="flex justify-around items-center">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                className={`relative flex flex-col items-center justify-center flex-1 py-2.5 px-2 rounded-2xl transition-all duration-300 ${
                  activeTab === item.id
                    ? 'bg-black dark:bg-white text-white dark:text-black scale-105'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100/30 dark:hover:bg-gray-800/30'
                }`}
              >
                <div className="mb-1">
                  {item.icon}
                </div>
                <span className="text-xs font-medium">{item.label}</span>
                {activeTab === item.id && (
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-black/5 dark:ring-white/5" />
                )}
                {item.id === 'profile' && hasBillingIssue && (
                  <span className="absolute top-2 right-3 flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Desktop/tablet left side navigation
  return (
    <div 
      className={`fixed left-4 top-1/2 transform -translate-y-1/2 z-[100] transition-all duration-300 ease-in-out ${
        isExpanded ? 'w-48' : 'w-16'
      }`}
      style={{ opacity: modalOpen ? 0 : 1, pointerEvents: modalOpen ? 'none' : 'auto' }}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div className="backdrop-blur-md bg-white/80 dark:bg-white/10 border border-gray-200 dark:border-white/20 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-3">
          {navItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`relative w-full flex items-center transition-all duration-200 rounded-xl p-3 mb-2 ${
                activeTab === item.id
                  ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 shadow-lg'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <div className="flex-shrink-0 flex items-center justify-center w-6 h-6">
                {item.icon}
              </div>
              <span 
                className={`ml-3 font-medium whitespace-nowrap transition-all duration-300 ${
                  isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                }`}
              >
                {item.label}
              </span>
              {item.id === 'profile' && hasBillingIssue && (
                <span className="absolute top-2 right-3 flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
                </span>
              )}
            </button>
          ))}
          
          {/* Theme toggle for desktop */}
          <button
            onClick={handleThemeToggle}
            className="w-full flex items-center transition-all duration-200 rounded-xl p-3 mb-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white"
          >
            <div className="flex-shrink-0 flex items-center justify-center w-6 h-6">
              {themeMode === 'dark' ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </div>
            <span 
              className={`ml-3 font-medium whitespace-nowrap transition-all duration-300 ${
                isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
              }`}
            >
              {themeMode === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </span>
          </button>
        </div>
        
        {/* Decorative gradient line */}
        <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-white/20 to-transparent" />
        
        {/* Brand indicator */}
        <div className="p-3">
          <div className={`flex items-center ${isExpanded ? 'justify-start' : 'justify-center'}`}>
            <div className="w-3 h-3 bg-blue-500 rounded-full flex-shrink-0"></div>
            <span 
              className={`ml-3 text-sm text-gray-700 dark:text-gray-300 font-bold transition-all duration-300 ${
                isExpanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
              }`}
            >
              Insight Atlas AI
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}